import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowRight, Mail, Linkedin, Facebook, Twitter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { name: 'All', slug: '' },
  { name: 'Entrepreneurship', slug: 'entrepreneurship' },
  { name: 'Economic Empowerment', slug: 'economic-empowerment' },
  { name: 'Closing the Gap', slug: 'closing-the-gap' },
  { name: 'Education & Growth', slug: 'education-growth' },
  { name: 'Leadership', slug: 'leadership' },
  { name: 'Tools & Systems', slug: 'tools-systems' },
  { name: 'Member Stories', slug: 'member-stories' },
  { name: 'Global Impact', slug: 'global-impact' },
  { name: 'Mindset & Resilience', slug: 'mindset-resilience' },
  { name: 'Women Who Changed the Rules', slug: 'women-who-changed-the-rules' },
];

const BlogHub = () => {
  const [articles, setArticles] = useState([]);
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const [pillarGuides, setPillarGuides] = useState([]);
  const [seriesArticles, setSeriesArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const articlesPerPage = 12;

  useEffect(() => {
    fetchData();
  }, [selectedCategory, searchQuery, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        is_published: true,
        limit: articlesPerPage,
        skip: (currentPage - 1) * articlesPerPage
      };
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;

      const [articlesRes, featuredRes, pillarsRes, seriesRes, popularRes, categoriesRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/articles`, { params }),
        axios.get(`${API_URL}/api/articles/featured`),
        axios.get(`${API_URL}/api/articles/pillar-guides`),
        axios.get(`${API_URL}/api/articles/series/Women Who Changed the Rules`),
        axios.get(`${API_URL}/api/articles/popular`),
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/articles/count`, { params: { category: selectedCategory, search: searchQuery, is_published: true } })
      ]);

      setArticles(articlesRes.data.filter(a => !a.is_featured && !a.is_pillar_guide));
      setFeaturedArticle(featuredRes.data);
      setPillarGuides(pillarsRes.data);
      setSeriesArticles(seriesRes.data.slice(0, 4));
      setPopularArticles(popularRes.data);
      setCategories(categoriesRes.data);
      setTotalArticles(countRes.data.count || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load articles');
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(totalArticles / articlesPerPage);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription failed');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-white py-12 md:py-24 px-4 sm:px-6 text-center border-b border-[#E2E8F0]">
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#2C2C2C] mb-4" data-testid="blog-title">
          The Journal
        </h1>
        <p className="font-label text-sm text-[#9D6E6D] tracking-widest mb-6" data-testid="blog-subtitle">
          INSIGHTS, STORIES & RESOURCES FOR WOMEN WHO BUILD
        </p>
        <p className="font-body text-[#767C91] max-w-2xl mx-auto mb-8">
          Real knowledge. Honest stories. Practical tools. For female entrepreneurs and professionals navigating 2026 with intelligence, purpose, and solidarity.
        </p>
        
        {/* Search Bar */}
        <div className="max-w-md mx-auto relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#767C91]" />
          <Input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-3 border-[#E2E8F0] focus:border-[#9D6E6D] rounded-none"
            data-testid="search-input"
          />
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white py-6 px-4 sm:px-6 border-b border-[#E2E8F0] overflow-x-auto">
        <div className="flex gap-3 justify-center flex-wrap max-w-6xl mx-auto">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setSelectedCategory(cat.slug)}
              className={`category-pill whitespace-nowrap ${selectedCategory === cat.slug ? 'active' : ''}`}
              data-testid={`category-${cat.slug || 'all'}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8 sm:space-y-12 overflow-hidden">
            {/* Featured Article */}
            {featuredArticle && !selectedCategory && !searchQuery && (
              <section data-testid="featured-article">
                <Link to={`/en/blog/${featuredArticle.slug}`}>
                  <div className="featured-article relative rounded-sm overflow-hidden">
                    <img 
                      src={featuredArticle.hero_image} 
                      alt={featuredArticle.hero_image_alt}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      loading="eager"
                      width="800"
                      height="600"
                    />
                    <div className="content">
                      <span className="font-label text-xs text-[#ABBDDF] tracking-widest">
                        {featuredArticle.category}
                      </span>
                      <h2 className="font-heading text-2xl md:text-3xl text-white mt-2 mb-3">
                        {featuredArticle.title}
                      </h2>
                      <p className="font-body text-white/80 text-sm mb-4 line-clamp-2">
                        {featuredArticle.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-white/60 text-xs font-label">
                        <span>{featuredArticle.author}</span>
                        <span>•</span>
                        <span>{formatDate(featuredArticle.date_published)}</span>
                        <span>•</span>
                        <span>{featuredArticle.read_time} min read</span>
                      </div>
                      <span className="inline-flex items-center gap-2 mt-4 text-white font-label text-xs tracking-widest hover:text-[#ABBDDF] transition-colors">
                        Read Article <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </section>
            )}

            {/* Essential Reading - Pillar Guides */}
            {pillarGuides.length > 0 && !selectedCategory && !searchQuery && (
              <section data-testid="pillar-guides">
                <h2 className="font-heading text-2xl text-[#2C2C2C] mb-6">Essential Reading</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {pillarGuides.map((guide) => (
                    <Link 
                      key={guide.id} 
                      to={`/en/blog/${guide.slug}`}
                      className="block bg-[#F2E1E1] p-4 sm:p-6 hover:shadow-lg transition-shadow overflow-hidden"
                    >
                      <h3 className="font-heading text-base sm:text-lg text-[#2C2C2C] mb-2 break-words">{guide.title}</h3>
                      <p className="font-body text-[#767C91] text-sm mb-4 line-clamp-2 break-words">{guide.excerpt}</p>
                      <span className="font-label text-xs text-[#9D6E6D] tracking-widest inline-flex items-center gap-2">
                        Read the Guide <ArrowRight className="w-3 h-3" />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Women Who Changed the Rules Series */}
            {seriesArticles.length > 0 && !selectedCategory && !searchQuery && (
              <section data-testid="series-section">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-2xl text-[#2C2C2C]">Women Who Changed the Rules</h2>
                    <p className="font-body text-[#767C91] text-sm mt-1">
                      Stories of trailblazing women and the lessons they teach us today.
                    </p>
                  </div>
                  <Link 
                    to="/en/blog/women-who-changed-the-rules"
                    className="font-label text-xs text-[#9D6E6D] tracking-widest inline-flex items-center gap-2 hover:text-[#2C2C2C] transition-colors"
                  >
                    View All <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {seriesArticles.map((article) => (
                    <Link 
                      key={article.id} 
                      to={`/en/blog/${article.slug}`}
                      className="article-card group"
                    >
                      <div className="overflow-hidden">
                        <img 
                          src={article.hero_image} 
                          alt={article.hero_image_alt}
                          className="w-full h-48 object-cover object-top"
                          loading="lazy"
                          width="400"
                          height="200"
                        />
                      </div>
                      <div className="p-4 sm:p-5">
                        <span className="font-label text-xs text-[#9D6E6D] tracking-widest">
                          {article.category}
                        </span>
                        <h3 className="font-heading text-base sm:text-lg text-[#2C2C2C] mt-2 mb-2 line-clamp-2 group-hover:text-[#9D6E6D] transition-colors">
                          {article.title}
                        </h3>
                        <p className="font-body text-[#767C91] text-sm line-clamp-2">{article.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Article Grid */}
            <section data-testid="articles-grid">
              <h2 className="font-heading text-2xl text-[#2C2C2C] mb-6">
                {selectedCategory ? CATEGORIES.find(c => c.slug === selectedCategory)?.name : 'Latest Articles'}
              </h2>
              
              {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="article-card">
                      <div className="h-48 loading-skeleton"></div>
                      <div className="p-5 space-y-3">
                        <div className="h-4 w-20 loading-skeleton"></div>
                        <div className="h-6 loading-skeleton"></div>
                        <div className="h-4 loading-skeleton"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : articles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {articles.map((article) => (
                    <Link 
                      key={article.id} 
                      to={`/en/blog/${article.slug}`}
                      className="article-card group"
                      data-testid={`article-card-${article.slug}`}
                    >
                      <div className="overflow-hidden">
                        <img 
                          src={article.hero_image} 
                          alt={article.hero_image_alt}
                          className="w-full h-48 object-cover object-top"
                          loading="lazy"
                          width="400"
                          height="200"
                        />
                      </div>
                      <div className="p-4 sm:p-5">
                        <span className="font-label text-xs text-[#9D6E6D] tracking-widest">
                          {article.category}
                        </span>
                        <h3 className="font-heading text-base sm:text-lg text-[#2C2C2C] mt-2 mb-2 line-clamp-2 group-hover:text-[#9D6E6D] transition-colors">
                          {article.title}
                        </h3>
                        <p className="font-body text-[#767C91] text-sm line-clamp-2 mb-3">{article.excerpt}</p>
                        <div className="flex flex-wrap items-center gap-2 text-[#767C91] text-xs font-label">
                          <span className="truncate max-w-[100px]">{article.author}</span>
                          <span>•</span>
                          <span className="whitespace-nowrap">{formatDate(article.date_published)}</span>
                          <span>•</span>
                          <span className="whitespace-nowrap">{article.read_time} min</span>
                        </div>
                        <span className="inline-flex items-center gap-2 mt-3 text-[#9D6E6D] font-label text-xs tracking-widest group-hover:text-[#2C2C2C] transition-colors">
                          Read Article <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="font-body text-[#767C91]">No articles found matching your criteria.</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && !loading && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-[#E2E8F0] text-[#767C91] hover:border-[#9D6E6D] hover:text-[#9D6E6D] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-label text-xs tracking-widest"
                    data-testid="pagination-prev"
                  >
                    Previous
                  </button>
                  
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 7) {
                        pageNum = i + 1;
                      } else if (currentPage <= 4) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 3) {
                        pageNum = totalPages - 6 + i;
                      } else {
                        pageNum = currentPage - 3 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-10 h-10 flex items-center justify-center text-sm font-label transition-colors ${
                            currentPage === pageNum
                              ? 'bg-[#9D6E6D] text-white'
                              : 'border border-[#E2E8F0] text-[#767C91] hover:border-[#9D6E6D] hover:text-[#9D6E6D]'
                          }`}
                          data-testid={`pagination-page-${pageNum}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-[#E2E8F0] text-[#767C91] hover:border-[#9D6E6D] hover:text-[#9D6E6D] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-label text-xs tracking-widest"
                    data-testid="pagination-next"
                  >
                    Next
                  </button>
                </div>
              )}
              
              {/* Results count */}
              {!loading && totalArticles > 0 && (
                <p className="text-center text-[#767C91] text-sm mt-4 font-body">
                  Showing {((currentPage - 1) * articlesPerPage) + 1}-{Math.min(currentPage * articlesPerPage, totalArticles)} of {totalArticles} articles
                </p>
              )}
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-6">
            {/* CTA Card - Membership */}
            <div className="sidebar-card" data-testid="membership-cta">
              <h3 className="font-heading text-xl text-[#2C2C2C] mb-3">Become a Lady Hustler</h3>
              <p className="font-body text-[#767C91] text-sm mb-4">
                Join 1,900+ women building businesses, careers, and impact together.
              </p>
              <a 
                href="https://join.entrepreneurladyhustler.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button block text-center"
              >
                BECOME A LADY HUSTLER →
              </a>
            </div>

            {/* Newsletter */}
            <div className="sidebar-card periwinkle" data-testid="newsletter-signup">
              <h3 className="font-heading text-xl text-[#2C2C2C] mb-3">Get Resources Delivered Weekly</h3>
              <p className="font-body text-[#2C2C2C]/70 text-sm mb-4">
                Join 1,900+ Lady Hustlers receiving practical insights, tools, and stories every week.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-3 py-2 border border-[#E2E8F0] text-sm"
                  required
                  data-testid="newsletter-email"
                />
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-[#9D6E6D] text-white font-label text-xs"
                  data-testid="newsletter-submit"
                >
                  Subscribe
                </button>
              </form>
              <p className="text-xs text-[#2C2C2C]/50 mt-2">No spam. Unsubscribe anytime.</p>
            </div>

            {/* Popular Articles */}
            {popularArticles.length > 0 && (
              <div className="bg-white border border-[#E2E8F0] p-6" data-testid="popular-articles">
                <h3 className="font-heading text-xl text-[#2C2C2C] mb-4">Popular Articles</h3>
                <div className="space-y-4">
                  {popularArticles.map((article, index) => (
                    <Link 
                      key={article.id}
                      to={`/en/blog/${article.slug}`}
                      className="block group"
                    >
                      <span className="font-heading text-2xl text-[#ABBDDF]">{String(index + 1).padStart(2, '0')}</span>
                      <h4 className="font-heading text-sm text-[#2C2C2C] mt-1 group-hover:text-[#9D6E6D] transition-colors line-clamp-2">
                        {article.title}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Categories List */}
            <div className="bg-white border border-[#E2E8F0] p-6" data-testid="categories-list">
              <h3 className="font-heading text-xl text-[#2C2C2C] mb-4">Categories</h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.slug)}
                    className="flex items-center justify-between w-full py-2 text-left text-sm hover:text-[#9D6E6D] transition-colors"
                  >
                    <span className="font-body text-[#2C2C2C]">{category.name}</span>
                    <span className="font-label text-xs text-[#767C91]">{category.article_count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white border border-[#E2E8F0] p-6">
              <h3 className="font-heading text-xl text-[#2C2C2C] mb-4">Connect With Us</h3>
              <div className="flex gap-3">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors">
                  <Twitter className="w-4 h-4" />
                </a>
                <a href="mailto:hello@ladyhustler.com" className="w-10 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors">
                  <Mail className="w-4 h-4" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogHub;
